import '../ComponentNotificationConfig';
import KeyboardArrowUp from '@mui/icons-material/KeyboardArrowUp';
import React from 'react';
import { useTranslation } from 'react-i18next';

import {
  DialogStyled,
  DialogContainerStyled,
  DialogHeaderStyled,
  DialogBackButton,
  DialogContentStyled,
  DialogPaperStyled,
  DialogEmptyStateStyled,
  DialogScrollTopStyled,
  DialogFabStyled,
} from './styles/MobileListComponentStyles';
import AtomDivider from '../../../components/atoms/AtomDivider';
import AtomFlex from '../../../components/atoms/AtomFlex';
import AtomTypography from '../../../components/atoms/AtomTypography/AtomTypography';
import EmptyStateComponent from '../../../components/generics/EmptyStateComponent/EmptyStateComponent';
import ArrowBackIcon from '../../../components/icons/svg/ArrowBackIcon';
import CheckDoubleIcon from '../../../components/icons/svg/CheckDoubleIcon';
import GraduateOwlIcon from '../../../components/icons/svg/GraduateOwlIcon';
import { styleConfigs } from '../../../components/resources/styleConfigs';
import ScrollbarWrapper from '../../../components/wrappers/ScrollbarWrapper';
import { IStudentNotification } from '../../../operations/mutations/student/useMutationUpdateStudentSkillStatus/useMutationUpdateStudentSkillStatus';
import palette from '../../../theme/learnerPalette';
import CardNotificationComponent from '../CardNotificationComponent/CardNotificationComponent';

interface INotifications {
  newNotifications: IStudentNotification[];
  beforeNotifications: IStudentNotification[];
}

interface IMobileListContainerProps {
  data?: INotifications;
  open: boolean;
  onClose?: () => Promise<void>;
}

const ScrollTop = (props: { children: React.ReactElement }): JSX.Element => {
  const { children } = props;
  const handleClick = (event: React.MouseEvent<HTMLDivElement>): void => {
    const anchor = (event.target as HTMLDivElement).ownerDocument?.querySelector('#back-to-top-anchor');

    if (anchor) {
      anchor.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  };

  return <DialogScrollTopStyled onClick={handleClick}>{children}</DialogScrollTopStyled>;
};

const MobileListComponent: React.FC<IMobileListContainerProps> = (props: IMobileListContainerProps) => {
  const { data, open, onClose } = props;
  const { t } = useTranslation('ComponentNotification');

  return (
    <DialogStyled fullScreen open={open} PaperComponent={DialogPaperStyled}>
      <DialogContainerStyled>
        <DialogHeaderStyled>
          <AtomFlex direction="row" gap={styleConfigs.marginPadding.custom2}>
            <AtomFlex height={styleConfigs.dimensions.percent100} direction="column" justifyContent="flex-start">
              <DialogBackButton data-testid="arrow-back-button" onClick={onClose}>
                <ArrowBackIcon />
              </DialogBackButton>
            </AtomFlex>
            <AtomFlex>
              <AtomTypography variant="subtitle1">{t('NOTIFICATION_TEXT_TITLE')}</AtomTypography>
              <AtomTypography variant="subtitle2" color={palette.light.text.mistBlue}>
                {t('NOTIFICATION_TEXT_UNREAD_NOTIFICATION', { unread: data?.newNotifications.length.toString() })}
              </AtomTypography>
            </AtomFlex>
          </AtomFlex>
          <AtomFlex mr={styleConfigs.marginPadding.custom2}>
            <CheckDoubleIcon
              width={styleConfigs.dimensions.width26}
              height={styleConfigs.dimensions.height26}
              color={palette.light.icons.prussianBlue}
            />
          </AtomFlex>
        </DialogHeaderStyled>
      </DialogContainerStyled>
      <AtomDivider />
      {!data || (!data.newNotifications.length && !data.beforeNotifications.length) ? (
        <DialogEmptyStateStyled>
          <EmptyStateComponent
            title={t('NOTIFICATION_EMPTY_STATE_TITLE')}
            fontSizeTitle={styleConfigs.fonts.fontSize18}
            gapIconTitle={styleConfigs.marginPadding.empty}
            subtitle={t('NOTIFICATION_EMPTY_STATE_SUBTITLE')}
            fontSizeSubtitle={styleConfigs.fonts.fontSize12}
            paddingTopSubtitle={styleConfigs.marginPadding.empty}
            svgIconComponent={<GraduateOwlIcon />}
          />
        </DialogEmptyStateStyled>
      ) : (
        <>
          <ScrollbarWrapper>
            <div id="back-to-top-anchor" />
            {data.newNotifications.map((notification: IStudentNotification) => (
              <CardNotificationComponent key={notification.id.toString()} notificationDetails={notification} isMobile />
            ))}
            {data.beforeNotifications.map((notification: IStudentNotification) => (
              <CardNotificationComponent
                key={notification.id.toString()}
                notificationDetails={notification}
                beforeNotification
                isMobile
              />
            ))}
          </ScrollbarWrapper>
          <DialogContentStyled>
            <ScrollTop>
              <DialogFabStyled size="small" aria-label="scroll back to top">
                <KeyboardArrowUp />
              </DialogFabStyled>
            </ScrollTop>
          </DialogContentStyled>
        </>
      )}
    </DialogStyled>
  );
};

export default MobileListComponent;
